import { Badge, Box } from '@mantine/core';
import { IconBookmark, IconFlame, IconStar } from '@tabler/icons-react';

interface RibbonProps {
  size?: 'sm' | 'lg';
}

const CornerRibbon = ({
  size = 'lg',
  label,
  background,
  icon,
}: RibbonProps & { label: string; background: string; icon?: React.ReactNode }) => {
  const isSmall = size === 'sm';

  return (
    <Box
      style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: isSmall ? 52 : 90,
        height: isSmall ? 52 : 90,
        overflow: 'hidden',
        zIndex: 1,
        pointerEvents: 'none',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          bottom: isSmall ? 9 : 18,
          right: isSmall ? -18 : -28,
          width: isSmall ? 80 : 130,
          textAlign: 'center',
          transform: 'rotate(-45deg)',
          background,
          color: 'white',
          fontSize: isSmall ? 6 : 9,
          fontWeight: 700,
          letterSpacing: '0.05em',
          padding: isSmall ? '2px 0 2px 8px' : '4px 0 4px 6px',
          textTransform: 'uppercase' as const,
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isSmall ? 2 : 3,
        }}
      >
        {icon}
        {label}
      </Box>
    </Box>
  );
};

export const TopPickRibbon = ({ size = 'lg' }: RibbonProps) => (
  <CornerRibbon
    size={size}
    label="Top pick"
    icon={
      <IconStar
        size={size === 'sm' ? 7 : 10}
        fill="currentColor"
        style={{ flexShrink: 0 }}
      />
    }
    background="linear-gradient(135deg, #fd7e14, #fa5252)"
  />
);

/**
 * Pill variant of the Top pick marker for dense card grids (wizard picker)
 * where even the small corner ribbon's rotated 6px label is illegible. Same
 * gradient and star so it reads as the same brand signal. Position it
 * yourself (absolute, image bottom-right).
 */
export const TopPickBadge = () => (
  <Badge
    size="sm"
    variant="gradient"
    gradient={{ from: 'orange', to: 'red', deg: 135 }}
    leftSection={<IconStar size={10} fill="currentColor" style={{ flexShrink: 0 }} />}
    style={{ boxShadow: 'var(--mantine-shadow-sm)' }}
  >
    Top pick
  </Badge>
);

/**
 * Marks catalog recipes other households have been cooking lately, computed
 * nightly from cook events. Same slot and same weight as the Top pick marker,
 * which it supersedes — see recipeMark. Red-to-pink rather than the Top pick
 * orange-to-red: adjacent enough to read as the same family of endorsement,
 * distinct enough that a returning user can tell which one they're looking at.
 */
export const TrendingBadge = () => (
  <Badge
    size="sm"
    variant="gradient"
    gradient={{ from: 'red', to: 'pink', deg: 135 }}
    leftSection={<IconFlame size={10} fill="currentColor" style={{ flexShrink: 0 }} />}
    style={{ boxShadow: 'var(--mantine-shadow-sm)' }}
  >
    Trending
  </Badge>
);

/** Corner-ribbon form of the Trending marker, for the surfaces that use ribbons. */
export const TrendingRibbon = ({ size = 'lg' }: RibbonProps) => (
  <CornerRibbon
    size={size}
    label="Trending"
    icon={
      <IconFlame
        size={size === 'sm' ? 7 : 10}
        fill="currentColor"
        style={{ flexShrink: 0 }}
      />
    }
    background="linear-gradient(135deg, #fa5252, #e64980)"
  />
);

/**
 * Marks recipes from the user's cookbook on suggestion surfaces (meal plan,
 * alternatives picker). Takes precedence over TopPickRibbon: once a user
 * saves a top pick it graduates — their own adoption is the stronger trust
 * signal, and one ribbon beats two.
 */
export const CookbookRibbon = ({ size = 'lg' }: RibbonProps) => (
  <CornerRibbon
    size={size}
    label="Cookbook"
    icon={
      <IconBookmark
        size={size === 'sm' ? 7 : 10}
        fill="currentColor"
        style={{ flexShrink: 0 }}
      />
    }
    background="linear-gradient(135deg, #20c997, #0ca678)"
  />
);

export type RecipeMark = 'cookbook' | 'trending' | 'topPick' | null;

/**
 * Which single mark a recipe card carries. These all compete for the same
 * corner of the same image, so they don't stack — the most specific true thing
 * about this recipe wins and the rest stay silent.
 *
 *  1. cookbook — the user already saved it. Their own adoption outranks
 *     anything we or anyone else could tell them.
 *  2. trending — other households are cooking it now. Supersedes Top pick
 *     because it's evidence rather than opinion: a curated recipe that people
 *     are actually cooking has the better version of the same story, and
 *     "we like this" adds nothing on top of "everyone's making this".
 *  3. topPick — we vetted it by hand. The fallback claim, not a lesser one.
 *
 * Surfaces that never receive trending data simply never return 'trending',
 * so this is safe to adopt anywhere the old inline cookbook/curated check
 * lives today.
 */
export const recipeMark = ({
  isCookbook = false,
  isTrending = false,
  isCurated = false,
}: {
  isCookbook?: boolean;
  isTrending?: boolean;
  isCurated?: boolean;
}): RecipeMark => {
  if (isCookbook) return 'cookbook';
  if (isTrending) return 'trending';
  if (isCurated) return 'topPick';
  return null;
};
